import React, { useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import {
  organizerReportsDaywiseIncomeApi,
  organizerReportsTotalsApi,
  organizerReportsExpressDaywiseApi,
  organizerReportsExpressDayDetailsApi,
  organizerReportsExpressStatusApi,
  organizerReportsExpressSyncApi,
  organizerReportsExpressTotalsApi,
  organizerReportsManualPosDayDetailsApi,
  organizerReportsManualPosDaywiseApi,
  organizerReportsManualPosTotalsApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import "../dashboard.css";
import "./Reports.css";

/* ------------ Icons (inline SVG, white on dark KPI cards) ------------ */
const IconCalendar = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const IconClipboard = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
  </svg>
);
const IconClipboardCheck = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </svg>
);
const IconTicket = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 9a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" />
    <path d="M13 5v2M13 17v2M13 11v2" />
  </svg>
);
const IconDollar = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 6v12M15.5 9.5A2.5 2.5 0 0 0 13 8h-2a2.5 2.5 0 0 0 0 5h2a2.5 2.5 0 0 1 0 5h-2a2.5 2.5 0 0 1-2.5-2.5" />
  </svg>
);
const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const Reports = () => {
  const [activeTab, setActiveTab] = useState("tickets");
  const [daywise, setDaywise] = useState({ result: [], totalIncome: 0, totalOrders: 0, totalDiscount: 0 });
  const [totals, setTotals] = useState({ totalEvent: 0, totalOrder: 0, totalEarnings: 0, totalDiscount: 0 });
  const [expressStatus, setExpressStatus] = useState({ last_sync: null });
  const [expressAllowed, setExpressAllowed] = useState(null);
  const [expressTotals, setExpressTotals] = useState({ totalSales: 0, saleCount: 0, byPaymentMethod: [] });
  const [expressDaywise, setExpressDaywise] = useState({ result: [], totalSales: 0, saleCount: 0 });
  const [manualPosTotals, setManualPosTotals] = useState({ totalSales: 0, saleCount: 0, byPaymentMethod: [] });
  const [manualPosDaywise, setManualPosDaywise] = useState({ result: [], totalSales: 0, saleCount: 0 });
  const [expressSyncing, setExpressSyncing] = useState(false);
  const [expandedExpressDate, setExpandedExpressDate] = useState(null);
  const [expandedManualPosDate, setExpandedManualPosDate] = useState(null);
  const [expressSalesByDate, setExpressSalesByDate] = useState({});
  const [expressArticlesByDate, setExpressArticlesByDate] = useState({});
  const [manualPosByDate, setManualPosByDate] = useState({});
  const [expandedExpressOpByDate, setExpandedExpressOpByDate] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add("finance-mobile-view");
    return () => document.body.classList.remove("finance-mobile-view");
  }, []);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );
  const numberFormatter = useMemo(() => new Intl.NumberFormat("es-AR"), []);

  const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
  const formatNumber = (value) => numberFormatter.format(Number(value || 0));
  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value === "string") {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const labelValue = (label, value) => {
    if (!value && value !== 0) return null;
    const text = String(value).trim();
    if (!text) return null;
    return (
      <span style={{ marginRight: 12 }}>
        <span className="text-muted">{label}:</span> {text}
      </span>
    );
  };

  const refreshExpressData = async () => {
    const s = await organizerReportsExpressStatusApi();
    const statusInfo = s?.data?.info || { last_sync: null, allowed: false };
    const allowed = Boolean(statusInfo?.allowed);

    setExpressAllowed(allowed);
    setExpressStatus(statusInfo);

    if (!allowed) {
      setExpressTotals({ totalSales: 0, saleCount: 0, byPaymentMethod: [] });
      setExpressDaywise({ result: [], totalSales: 0, saleCount: 0 });
      return { statusInfo, totalsInfo: null, daywiseInfo: null };
    }

    const [t, d] = await Promise.all([
      organizerReportsExpressTotalsApi({ days: 10, includeToday: 1 }),
      organizerReportsExpressDaywiseApi({ days: 10, includeToday: 1 }),
    ]);
    const totalsInfo = t?.data?.info || { totalSales: 0, saleCount: 0, byPaymentMethod: [] };
    const daywiseInfo = d?.data?.info || { result: [], totalSales: 0, saleCount: 0 };
    setExpressTotals(totalsInfo);
    setExpressDaywise(daywiseInfo);
    return { statusInfo, totalsInfo, daywiseInfo };
  };

  const refreshManualPosData = async () => {
    const [t, d] = await Promise.all([
      organizerReportsManualPosTotalsApi({ days: 10, includeToday: 1 }),
      organizerReportsManualPosDaywiseApi({ days: 10, includeToday: 1 }),
    ]);
    const totalsInfo = t?.data?.info || { totalSales: 0, saleCount: 0, byPaymentMethod: [] };
    const daywiseInfo = d?.data?.info || { result: [], totalSales: 0, saleCount: 0 };
    setManualPosTotals(totalsInfo);
    setManualPosDaywise(daywiseInfo);
    return { totalsInfo, daywiseInfo };
  };

  const loadExpressDayDetailsForDate = async (date) => {
    if (!date) return;
    if (expressSalesByDate[date] && expressArticlesByDate[date]) return;
    if (expressAllowed === false) return;

    const res = await organizerReportsExpressDayDetailsApi({ date, includeToday: 1, limit: 5000 });
    const sales = res?.data?.info?.sales || [];
    const articles = res?.data?.info?.articles || [];

    setExpressSalesByDate((prev) => ({ ...prev, [date]: sales }));
    setExpressArticlesByDate((prev) => ({ ...prev, [date]: articles }));
  };

  const loadManualPosDetailsForDate = async (date) => {
    if (!date) return;
    if (manualPosByDate[date]) return;
    const res = await organizerReportsManualPosDayDetailsApi({ date, includeToday: 1, limit: 5000 });
    const rows = res?.data?.info?.rows || [];
    setManualPosByDate((prev) => ({ ...prev, [date]: rows }));
  };

  const triggerExpressSync = async () => {
    setExpressSyncing(true);
    try {
      if (expressAllowed === false) return;
      const beforeData = await refreshExpressData().catch(() => null);
      const before = beforeData?.statusInfo?.last_sync ? String(beforeData.statusInfo.last_sync) : "";
      await organizerReportsExpressSyncApi(true);

      let didSync = false;
      const startedAt = Date.now();
      while (Date.now() - startedAt < 90_000) {
        await new Promise((r) => setTimeout(r, 5_000));
        const afterData = await refreshExpressData().catch(() => null);
        const after = afterData?.statusInfo?.last_sync ? String(afterData.statusInfo.last_sync) : "";
        if (after && after !== before) {
          didSync = true;
          break;
        }
      }

      if (didSync) {
        setExpandedExpressDate(null);
        setExpandedExpressOpByDate({});
        setExpressSalesByDate({});
        setExpressArticlesByDate({});
      }
    } catch (e) {
      handleError(e);
    } finally {
      setExpressSyncing(false);
    }
  };

  const handleActualizar = async () => {
    setExpandedExpressDate(null);
    setExpandedManualPosDate(null);
    setExpandedExpressOpByDate({});
    setExpressSalesByDate({});
    setExpressArticlesByDate({});
    setManualPosByDate({});
    await Promise.all([refreshExpressData().catch(handleError), refreshManualPosData().catch(handleError)]);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, t] = await Promise.all([organizerReportsDaywiseIncomeApi(), organizerReportsTotalsApi()]);
        setDaywise(d?.data?.info || { result: [], totalIncome: 0, totalOrders: 0, totalDiscount: 0 });
        setTotals(t?.data?.info || { totalEvent: 0, totalOrder: 0, totalEarnings: 0, totalDiscount: 0 });
        await Promise.all([refreshExpressData(), refreshManualPosData()]);
      } catch (e) {
        handleError(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <SpinLoading />;

  const posVisible = expressAllowed !== false;
  const posSaleCount = Number(expressTotals.saleCount || 0) + Number(manualPosTotals.saleCount || 0);
  const posTotalSales = Number(expressTotals.totalSales || 0) + Number(manualPosTotals.totalSales || 0);
  const manualPosRowsWithSales = (manualPosDaywise.result || []).filter(
    (r) => Number(r.totalSales || 0) > 0 || Number(r.saleCount || 0) > 0
  );
  const expressRowsWithSales = (expressDaywise.result || []).filter(
    (r) => Number(r.totalSales || 0) > 0 || Number(r.saleCount || 0) > 0
  );

  return (
    <div className="organizer-page organizer-finanzas">
      <section className="organizer-section organizer-reports zoco-finanzas">
        <div className="zoco-kpi-row">
          <div className="zoco-kpi-card">
            <div className="zoco-kpi-card__icon"><IconCalendar /></div>
            <div className="zoco-kpi-card__body">
              <p className="zoco-kpi-card__label">Eventos</p>
              <p className="zoco-kpi-card__value">{formatNumber(totals.totalEvent)}</p>
            </div>
          </div>

          <div className="zoco-kpi-card">
            <div className="zoco-kpi-card__icon"><IconClipboard /></div>
            <div className="zoco-kpi-card__body">
              <p className="zoco-kpi-card__label">Pedidos</p>
              <p className="zoco-kpi-card__value">{formatNumber(totals.totalOrder)}</p>
            </div>
          </div>

          <div className="zoco-kpi-card">
            <div className="zoco-kpi-card__icon"><IconClipboardCheck /></div>
            <div className="zoco-kpi-card__body">
              <p className="zoco-kpi-card__label">N° ventas POS</p>
              <p className="zoco-kpi-card__value">{formatNumber(posSaleCount)}</p>
            </div>
          </div>

          <div className="zoco-kpi-stack">
            <div className="zoco-kpi-card zoco-kpi-card--row">
              <div className="zoco-kpi-card__icon"><IconTicket /></div>
              <div className="zoco-kpi-card__body">
                <p className="zoco-kpi-card__label">Ventas tickets</p>
                <p className="zoco-kpi-card__value">{formatCurrency(totals.totalEarnings)}</p>
              </div>
            </div>
            <div className="zoco-kpi-card zoco-kpi-card--row">
              <div className="zoco-kpi-card__icon"><IconDollar /></div>
              <div className="zoco-kpi-card__body">
                <p className="zoco-kpi-card__label">Ventas POS</p>
                <p className="zoco-kpi-card__value">{formatCurrency(posTotalSales)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="zoco-tabs" role="tablist" aria-label="Secciones de finanzas">
          <button
            type="button"
            className={`zoco-tab ${activeTab === "tickets" ? "zoco-tab--active" : ""}`}
            onClick={() => setActiveTab("tickets")}
          >
            Tickets
          </button>
          <button
            type="button"
            className={`zoco-tab ${activeTab === "pos" ? "zoco-tab--active" : ""}`}
            onClick={() => setActiveTab("pos")}
          >
            Ventas POS
          </button>
        </div>

        {/* ===== Two-column tables ===== */}
        <div className="zoco-tables-row">
          {/* ----- Ingresos ----- */}
          {activeTab === "tickets" && (
          <div className="zoco-panel">
            <div className="zoco-panel__head">
              <div>
                <h3 className="zoco-panel__title">
                  Ingresos <span className="zoco-panel__title-thin">| últimos 7 dias</span>
                </h3>
                <p className="zoco-panel__subtitle">
                  <span className="zoco-panel__subtitle-lbl">Total de ingresos</span> {formatCurrency(daywise.totalIncome)} | <span className="zoco-panel__subtitle-lbl">Pedidos totales</span> {formatNumber(daywise.totalOrders)}
                </p>
              </div>
            </div>

            <table className="zoco-table">
              <thead>
                <tr>
                  <th className="zoco-table__col-date">Fecha</th>
                  <th className="zoco-table__col-amount">Ingresos</th>
                  <th className="zoco-table__col-qty-right">Pedidos</th>
                </tr>
              </thead>
              <tbody>
                {(daywise.result || []).map((r, idx) => (
                  <tr key={r.date || `${idx}-${r.totalIncome}`}>
                    <td className="zoco-table__col-date">{formatDate(r.date)}</td>
                    <td className="zoco-table__col-amount">{formatCurrency(r.totalIncome)}</td>
                    <td className="zoco-table__col-qty-right">{formatNumber(r.orderCount)}</td>
                  </tr>
                ))}
                {(daywise?.result || []).length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", color: "var(--zoco-muted)" }}>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* ----- Ventas POS ----- */}
          {activeTab === "pos" && (
            <div className="zoco-panel">
              <div className="zoco-panel__head">
                <div>
                  <h3 className="zoco-panel__title">
                    Ventas POS importadas <span className="zoco-panel__title-thin">| últimos 10 dias</span>
                  </h3>
                  <p className="zoco-panel__subtitle">
                    <span className="zoco-panel__subtitle-lbl">Total</span> {formatCurrency(manualPosDaywise.totalSales)} | <span className="zoco-panel__subtitle-lbl">Ventas</span> {formatNumber(manualPosDaywise.saleCount)}
                  </p>
                </div>
                <div className="zoco-panel__actions">
                  <button
                    type="button"
                    className="zoco-btn zoco-btn--ghost"
                    onClick={handleActualizar}
                    disabled={expressSyncing}
                  >
                    Actualizar
                  </button>
                </div>
              </div>

              <table className="zoco-table">
                <thead>
                  <tr>
                    <th className="zoco-table__col-date">Fecha</th>
                    <th className="zoco-table__col-amount">Ventas</th>
                    <th className="zoco-table__col-qty-center">Cantidad</th>
                    <th className="zoco-table__col-chev" aria-label="" />
                  </tr>
                </thead>
                <tbody>
                  {manualPosRowsWithSales.map((r, idx) => {
                    const date = r.date;
                    const expanded = expandedManualPosDate === date;
                    const rows = manualPosByDate[date] || null;
                    const onToggleDay = async () => {
                      const next = expanded ? null : date;
                      setExpandedManualPosDate(next);
                      if (!expanded) {
                        try {
                          await loadManualPosDetailsForDate(date);
                        } catch (e) {
                          handleError(e);
                        }
                      }
                    };

                    return (
                      <React.Fragment key={date || `${idx}-${r.totalSales}`}>
                        <tr
                          className={`zoco-row--expandable ${expanded ? "zoco-row--expanded" : ""}`}
                          onClick={onToggleDay}
                        >
                          <td className="zoco-table__col-date">{formatDate(date)}</td>
                          <td className="zoco-table__col-amount">{formatCurrency(r.totalSales)}</td>
                          <td className="zoco-table__col-qty-center">{formatNumber(r.saleCount)}</td>
                          <td className="zoco-table__col-chev">
                            <span className={`zoco-row__chev ${expanded ? "zoco-row__chev--open" : ""}`}>
                              <IconChevronRight />
                            </span>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="zoco-row__detail">
                            <td colSpan={4}>
                              {!rows && <div className="zoco-detail-loading">Cargando operaciones...</div>}
                              {rows && rows.length === 0 && <div className="zoco-detail-empty">Sin operaciones</div>}
                              {rows && rows.length > 0 && (
                                <Table responsive className="align-middle mb-0">
                                  <thead>
                                    <tr>
                                      <th>Hora</th>
                                      <th>ID pago</th>
                                      <th>Total</th>
                                      <th>Pago</th>
                                      <th>Empleado</th>
                                      <th>Terminal</th>
                                      <th>Recibo</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((op) => (
                                      <tr key={op._id || op.payment_id} onClick={(e) => e.stopPropagation()}>
                                        <td>{formatTime(op.payment_date)}</td>
                                        <td>{op.payment_id || "-"}</td>
                                        <td>{formatCurrency(op.amount)}</td>
                                        <td>{op.payment_method || op.card_brand || "-"}</td>
                                        <td>{op.payment_employee_name || op.payment_employee_id || "-"}</td>
                                        <td>{op.terminal_id || op.device || "-"}</td>
                                        <td>{op.receipt_number || op.authorization_code || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {manualPosRowsWithSales.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", color: "var(--zoco-muted)" }}>
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "pos" && posVisible && (
            <div className="zoco-panel">
              <div className="zoco-panel__head">
                <div>
                  <h3 className="zoco-panel__title">
                    Ventas POS <span className="zoco-panel__title-thin">| últimos 7 dias</span>
                  </h3>
                  <p className="zoco-panel__subtitle">
                    <span className="zoco-panel__subtitle-lbl">Total</span> {formatCurrency(expressDaywise.totalSales)} | <span className="zoco-panel__subtitle-lbl">Ventas</span> {formatNumber(expressDaywise.saleCount)}
                    {expressStatus?.last_sync ? (
                      <>
                        {" "}
                        | <span className="zoco-panel__subtitle-lbl">Ult. sync</span> {formatDate(expressStatus.last_sync)}
                      </>
                    ) : ""}
                    {expressSyncing ? " | Sincronizando..." : ""}
                  </p>
                </div>
                <div className="zoco-panel__actions">
                  <button
                    type="button"
                    className="zoco-btn zoco-btn--ghost"
                    onClick={handleActualizar}
                    disabled={expressSyncing}
                  >
                    Actualizar
                  </button>
                  <button
                    type="button"
                    className="zoco-btn zoco-btn--dark"
                    onClick={triggerExpressSync}
                    disabled={expressSyncing}
                  >
                    {expressSyncing ? "Sincronizando..." : "Sincronizar"}
                  </button>
                </div>
              </div>

              <table className="zoco-table">
                <thead>
                  <tr>
                    <th className="zoco-table__col-date">Fecha</th>
                    <th className="zoco-table__col-amount">Ventas</th>
                    <th className="zoco-table__col-qty-center">Cantidad</th>
                    <th className="zoco-table__col-chev" aria-label="" />
                  </tr>
                </thead>
                <tbody>
                  {expressRowsWithSales.map((r, idx) => {
                    const date = r.date;
                    const expanded = expandedExpressDate === date;
                    const rows = expressSalesByDate[date] || null;
                    const articleRows = expressArticlesByDate[date] || null;

                    const onToggleDay = async () => {
                      const next = expanded ? null : date;
                      setExpandedExpressDate(next);
                      if (!expanded) {
                        try {
                          await loadExpressDayDetailsForDate(date);
                        } catch (e) {
                          handleError(e);
                        }
                      }
                    };

                    return (
                      <React.Fragment key={date || `${idx}-${r.totalSales}`}>
                        <tr
                          className={`zoco-row--expandable ${expanded ? "zoco-row--expanded" : ""}`}
                          onClick={onToggleDay}
                        >
                          <td className="zoco-table__col-date">{formatDate(date)}</td>
                          <td className="zoco-table__col-amount">{formatCurrency(r.totalSales)}</td>
                          <td className="zoco-table__col-qty-center">{formatNumber(r.saleCount)}</td>
                          <td className="zoco-table__col-chev">
                            <span className={`zoco-row__chev ${expanded ? "zoco-row__chev--open" : ""}`}>
                              <IconChevronRight />
                            </span>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="zoco-row__detail">
                            <td colSpan={4}>
                              {!rows && <div className="zoco-detail-loading">Cargando operaciones...</div>}
                              {rows && rows.length === 0 && <div className="zoco-detail-empty">Sin operaciones</div>}
                              {rows && rows.length > 0 && (
                                <Table responsive className="align-middle mb-0">
                                  <thead>
                                    <tr>
                                      <th>Hora</th>
                                      <th>Ticket</th>
                                      <th>Total</th>
                                      <th>Pago</th>
                                      <th>Cajero</th>
                                      <th>Terminal</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((op) => {
                                      const expandedOpId = expandedExpressOpByDate[date] || null;
                                      const isExpanded = expandedOpId === op.express_id;
                                      const terminalText =
                                        op.terminal || op.terminal_description || op.store_name || op.chain_name || "-";
                                      const cashierText = op.cashier || "-";
                                      const opMs = op.close_date ? new Date(op.close_date).getTime() : null;

                                      const items = (() => {
                                        if (!opMs || !Array.isArray(articleRows)) return [];
                                        const candidates = articleRows.filter((it) => typeof it?.closeDate === "number");
                                        if (candidates.length === 0) return [];
                                        const windowMs = 5 * 60 * 1000;
                                        const t = terminalText && terminalText !== "-" ? terminalText : null;
                                        const store = op.store_name || null;
                                        const chain = op.chain_name || null;
                                        const byTime = (it) => Math.abs(it.closeDate - opMs) <= windowMs;
                                        const byTerminal = (it) => (t ? String(it.terminal || "") === t : true);
                                        const byStore = (it) => (store ? String(it.store || "") === store : true);
                                        const byChain = (it) => (chain ? String(it.chain || "") === chain : true);
                                        const strict = candidates.filter((it) => byTime(it) && byTerminal(it));
                                        if (strict.length > 0) return strict;
                                        const relaxed = candidates.filter((it) => byTime(it) && byStore(it) && byChain(it));
                                        if (relaxed.length > 0) return relaxed;
                                        return candidates.filter((it) => byTime(it));
                                      })();

                                      return (
                                        <React.Fragment key={op.express_id}>
                                          <tr onClick={(e) => e.stopPropagation()}>
                                            <td>{formatTime(op.close_date)}</td>
                                            <td>{op.ticket_number || "-"}</td>
                                            <td>{formatCurrency(op.total)}</td>
                                            <td>{op.payment_method || "-"}</td>
                                            <td>{cashierText}</td>
                                            <td>{terminalText}</td>
                                            <td className="text-end">
                                              <button
                                                type="button"
                                                className="zoco-btn zoco-btn--ghost"
                                                style={{ height: 28, padding: "0 10px", fontSize: 12 }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedExpressOpByDate((prev) => ({
                                                    ...prev,
                                                    [date]: isExpanded ? null : op.express_id,
                                                  }));
                                                }}
                                              >
                                                {isExpanded ? "Menos" : "Detalle"}
                                              </button>
                                            </td>
                                          </tr>
                                          {isExpanded && (
                                            <tr onClick={(e) => e.stopPropagation()}>
                                              <td colSpan={7} style={{ background: "rgba(0,0,0,0.02)" }}>
                                                <div style={{ padding: "8px 4px" }}>
                                                  <div style={{ marginBottom: 6 }}>
                                                    {labelValue("Doc", op.document_type)}
                                                    {labelValue("Serie/Folio", op.serie_folio)}
                                                    {labelValue("AFIP", op.afip_document_type)}
                                                    {labelValue("Entrega", op.invoice_delivery_method)}
                                                    {labelValue("Moneda", op.currency)}
                                                    {labelValue("Lista", op.price_list_id)}
                                                    {labelValue("CXC", op.cxc)}
                                                  </div>
                                                  <div>
                                                    {labelValue("Sucursal", op.store_name)}
                                                    {labelValue("Cadena", op.chain_name)}
                                                    {labelValue("Terminal", op.terminal_description)}
                                                    {labelValue("Grupo", op.groups_descriptions)}
                                                    {labelValue("Afectado", op.affected_amount)}
                                                  </div>
                                                  <div style={{ marginTop: 10 }}>
                                                    <div className="text-muted" style={{ marginBottom: 6 }}>
                                                      Artículos
                                                    </div>
                                                    {!articleRows && <div className="text-muted">Cargando artículos...</div>}
                                                    {articleRows && items.length === 0 && (
                                                      <div className="text-muted">Sin artículos para esta operación</div>
                                                    )}
                                                    {articleRows && items.length > 0 && (
                                                      <Table responsive className="align-middle mb-0">
                                                        <thead>
                                                          <tr>
                                                            <th>Artículo</th>
                                                            <th>Cant.</th>
                                                            <th>Monto</th>
                                                            <th>Cód. barras</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {items.map((it, idx2) => (
                                                            <tr key={`${op.express_id}-${it.articleCode}-${idx2}`}>
                                                              <td>{it.description || it.articleCode || "-"}</td>
                                                              <td>{formatNumber(it.quantity)}</td>
                                                              <td>{formatCurrency(it.cost)}</td>
                                                              <td>
                                                                {Array.isArray(it.barCodes) && it.barCodes.length > 0
                                                                  ? it.barCodes.join(", ")
                                                                  : "-"}
                                                              </td>
                                                            </tr>
                                                          ))}
                                                        </tbody>
                                                      </Table>
                                                    )}
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {expressRowsWithSales.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", color: "var(--zoco-muted)" }}>
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ===== Payments breakdown (kept from previous version) ===== */}
        {activeTab === "pos" && (manualPosTotals?.byPaymentMethod || []).length > 0 && (
          <div className="zoco-panel" style={{ marginTop: 24 }}>
            <div className="zoco-panel__head">
              <div>
                <h3 className="zoco-panel__title">Medios de pago importados</h3>
                <p className="zoco-panel__subtitle">Distribución de ventas POS cargadas desde admin.</p>
              </div>
            </div>
            <table className="zoco-table">
              <thead>
                <tr>
                  <th>Medio de pago</th>
                  <th>Ventas</th>
                  <th>Tickets</th>
                </tr>
              </thead>
              <tbody>
                {manualPosTotals.byPaymentMethod.map((p) => (
                  <tr key={p.payment_method || "unknown"}>
                    <td>{p.payment_method || "-"}</td>
                    <td>{formatCurrency(p.totalSales)}</td>
                    <td>{formatNumber(p.saleCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "pos" && posVisible && (expressTotals?.byPaymentMethod || []).length > 0 && (
          <div className="zoco-panel" style={{ marginTop: 24 }}>
            <div className="zoco-panel__head">
              <div>
                <h3 className="zoco-panel__title">Medios de pago</h3>
                <p className="zoco-panel__subtitle">Distribución de ventas POS por medio de pago.</p>
              </div>
            </div>
            <table className="zoco-table">
              <thead>
                <tr>
                  <th>Medio de pago</th>
                  <th>Ventas</th>
                  <th>Tickets</th>
                </tr>
              </thead>
              <tbody>
                {expressTotals.byPaymentMethod.map((p) => (
                  <tr key={p.payment_method || "unknown"}>
                    <td>{p.payment_method || "-"}</td>
                    <td>{formatCurrency(p.totalSales)}</td>
                    <td>{formatNumber(p.saleCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Reports;
